import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@vigia/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Avatar storage key (`avatars/{userId}/{hash}.webp`).
   *
   * It lives here and is not read from the session on purpose: the session is served by the API and
   * stays cached, so a freshly uploaded photo would not be reflected until the
   * next refresh. By reading it from the local row —same as the language, the
   * font and the theme— the photo appears instantly and survives offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * ALWAYS filters by `userId`, same as the rest of the `user` reads in this
 * module. It used to decide the filter in one branch and the guest was left with
 * `SELECT … FROM user LIMIT 1` flat: without `WHERE`, SQLite returns the FIRST
 * row that suits it, which does not have to be the guest's. That is not a
 * correctness bug but a data leak between accounts: in the guest→real
 * upgrade the local DB pulls one `user` row per co-member of the
 * organization, so the guest ended up painting ANOTHER person's theme.
 * Without `WHERE` no empty result is possible: it always returns somebody's row.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
